import { RefObject } from 'preact';
import { useRef } from 'preact/hooks';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { classNames } from '#utils/UNSAFE_classNames';
import { StyledComponentProps } from '@oracle/oraclejet-internal-utilities/typeUtils';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { CompactLabelAssistance } from '#UNSAFE_UserAssistance';

import { useDetectLabelTruncation } from './useDetectLabelTruncation';
import { useTooltip } from '#hooks/UNSAFE_useTooltip';

import { LabelStyles, LabelVariantOptions } from './themes/LabelStyles.css';
import { TextFieldRedwoodTheme } from '#UNSAFE_TextField/themes/redwood/TextFieldTheme';
import { LabelRedwoodTheme } from './themes/redwood/LabelTheme';
import { useScale } from '#hooks/UNSAFE_useScale';
import { useDensity } from '#hooks/UNSAFE_useDensity';
import { getClientHints } from '#utils/PRIVATE_clientHints';

type StyleProps = Exclude<Props, 'children' | 'forId' | 'id'> & {
  hasValue?: boolean;
  isFocused?: boolean;
  readonly?: boolean;
  disabled?: boolean;
};

type StyledLabelProps = StyledComponentProps<'label', StyleProps> & {
  onClick?: (e: MouseEvent) => void;
};

type TruncatingLabelProps = Omit<
  StyledLabelProps,
  | 'readonly'
  | 'disabled'
  | 'isRequiredShown'
  | 'helpIconId'
  | 'helpSourceLink'
  | 'helpSourceText'
  | 'parentComponentVariant'
  | 'userAssistanceDensity'
  | 'variant'
>;

// When you mouse over the label, useDetectLabelTruncation determines if the text is truncated
// and shows a tooltip. When you move the mouse outside of the label, the tooltip goes away.
const TruncatingLabel = ({
  children: text,
  labelRef,
  hasValue,
  isFocused,
  ...labelProps
}: TruncatingLabelProps) => {
  const labelElementRef = useRef<HTMLLabelElement>(null);
  const ref = labelRef ?? labelElementRef;
  const { detectTruncationProps, isTextTruncated } = useDetectLabelTruncation({ rootRef: ref });

  // Calculate the tooltip offset so it looks like it's relative to the field instead of the label.
  const scale = useScale() || 'lg';
  const density = useDensity() || 'standard';
  const scaleMap = { sm: 3, md: 3.5, lg: 4 };
  const xUnits = scaleMap[scale];
  const densityUnits = density === 'standard' ? 0 : -xUnits;
  const mainAxisMap = {
    sm: Math.max(xUnits + densityUnits, 3),
    md: Math.max(1.14 * xUnits + densityUnits, 3.5),
    lg: 2 * xUnits + densityUnits
  };
  const mainAxis =
    hasValue || isFocused ? mainAxisMap[scale] + 5 : Math.max(5.75 * xUnits + densityUnits, 16);
  const { tooltipContent, tooltipProps } = useTooltip({
    offset: {
      mainAxis,
      crossAxis: -4 * xUnits - 1
    },
    position: 'top-start',
    text,
    isDisabled: !isTextTruncated
  });
  const { 'aria-describedby': notNeeded, ...tooltipEventHandlerProps } = tooltipProps;

  return (
    <>
      <label ref={ref} {...detectTruncationProps} {...labelProps} {...tooltipEventHandlerProps}>
        {text}
      </label>
      {tooltipContent}
    </>
  );
};

const StyledLabel = ({
  hasValue,
  readonly,
  disabled,
  isFocused,
  isRequiredShown,
  labelRef,
  helpIconId,
  helpSourceLink,
  helpSourceText,
  userAssistanceDensity = 'reflow',
  variant = 'inside',
  parentComponentVariant = 'textField',
  ...props
}: StyledLabelProps) => {
  const { isFormLayout, isReadonly: isReadonlyForm, labelWrapping } = useFormContext();

  const isInside = variant === 'inside';
  const isTextField = parentComponentVariant === 'textField';
  const clientHints = getClientHints();

  // JET-73197: Truncated tooltips for core pack.
  // Determine whether to render a TruncatingLabel, which is measured to determine if
  // the label's content exceeds the size of the label itself, i.e. it's truncated.
  // We only want to do this for inside label edge, not disabled, and the textField variant.
  const useTruncatingLabel = isInside && !disabled && parentComponentVariant === 'textField';

  // because variants have to match strings, we have to take any boolean props and convert
  // them to a string value, (ie prefixed with "is" or "non")
  const {
    classes,
    styles: {
      safariAfter,
      uaDensityCompactLabel,
      uaDensityCompactLabelStart,
      uaDensityCompactLabelTopInside
    }
  } = useComponentTheme<LabelVariantOptions, LabelStyles>(LabelRedwoodTheme, {
    labelWrapping,
    position: variant,
    inside: isInside ? 'isInside' : 'notInside',
    inEnabledForm: isFormLayout && !isReadonlyForm ? 'isInEnabledForm' : 'notInEnabledForm',
    readonly: readonly ? 'isReadonly' : 'notReadonly',
    focused: isFocused ? 'isFocused' : 'nonFocused',
    formLayout: isFormLayout ? 'isFormLayout' : 'nonFormLayout',
    readonlyForm: isReadonlyForm ? 'isReadonlyForm' : 'notReadonlyForm',
    textField: isTextField ? 'isTextField' : 'notTextField',
    value: hasValue ? 'hasValue' : 'noValue',
    valueOrFocus: hasValue || isFocused ? 'hasValueOrFocus' : 'noValueOrFocus',
    animatedWhenInside: isTextField ? 'isAnimated' : 'notAnimated',
    disabled: isTextField && disabled ? 'isDisabled' : 'nonDisabled',
    userAssistanceDensity
  });

  const { baseTheme: textFieldTheme } = useComponentTheme(TextFieldRedwoodTheme);
  // JET-74754: Fix dual tooltips on Safari. This prevents safari from putting
  // up its own tooltip for truncated labels.
  const afterClass = clientHints.browser === 'safari' ? safariAfter : undefined;

  // TODO: We need to decide whether to standardize on 'class' or 'className'.  Otherwise, we'll
  // run into issues destructuring style interpolations, for example having to concatenate both.

  const compactLabelStyles = classNames([
    afterClass,
    uaDensityCompactLabel,
    variant === 'start' ? uaDensityCompactLabelStart : uaDensityCompactLabelTopInside
  ]);
  const compactUADLabel = useTruncatingLabel ? (
    <TruncatingLabel
      {...props}
      class={compactLabelStyles}
      hasValue={hasValue}
      isFocused={isFocused}
      labelRef={labelRef}
    />
  ) : (
    <label {...props} class={compactLabelStyles} />
  );

  return userAssistanceDensity === 'compact' ? (
    <div class={classNames([classes, textFieldTheme])}>
      {variant !== 'start' && compactUADLabel}
      <CompactLabelAssistance
        isRequiredShown={isRequiredShown}
        helpIconId={helpIconId}
        helpSourceLink={helpSourceLink}
        helpSourceText={helpSourceText}
        labelEdge={variant === 'top' || variant === 'start' ? variant : 'inside'}
      />
      {variant === 'start' && compactUADLabel}
    </div>
  ) : useTruncatingLabel ? (
    <TruncatingLabel
      {...props}
      class={classNames([afterClass, classes, textFieldTheme])}
      hasValue={hasValue}
      isFocused={isFocused}
      labelRef={labelRef}
    />
  ) : (
    <label
      // JET-71582  Adding aria-disabled for components that don't have a disabled input element. AXE isn't able
      // to determine that the label belongs to a disabled field, so it throwing a contrast error in the webdriverjs
      // tests.
      aria-disabled={disabled ? 'true' : undefined}
      {...props}
      class={classNames([afterClass, classes, textFieldTheme])}
    />
  );
};

type Props = {
  /**
   * Specifies how messages and assistive text are shown.
   */
  userAssistanceDensity?: 'reflow' | 'efficient' | 'compact';
  /**
   * Id to use for the help icon in compact user assistance density.
   */
  helpIconId?: string;
  /**
   * Help source link for compact user assistance density.
   */
  helpSourceLink?: string;
  /**
   * Help source text for compact user assistance density.
   */
  helpSourceText?: string;
  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has committed a value.
   */
  isRequiredShown?: boolean;
  /**
   * The text to be used for the label
   */
  children?: string;
  /**
   * The id to set on the label
   */
  id?: string;
  /**
   * The id of the form control for which this label will be used
   */
  forId?: string;
  /**
   * Determines how the label is going to be styled
   */
  variant?: 'inside' | 'start' | 'top';
  /**
   * Determines how the label is going to be styled in based on parent component.
   */
  parentComponentVariant?:
    | 'textField'
    | 'radioSet'
    | 'checkboxSet'
    | 'richRadioSet'
    | 'richCheckboxSet'
    | 'switch'
    | 'formComponentWrapper';
  /**
   * Handler called when the label is clicked.
   */
  onClick?: (e: MouseEvent) => void;
  /**
   * A ref to the intrinsic label.
   */
  labelRef?: RefObject<HTMLLabelElement>;
};

export const Label = ({ forId, ...props }: Props) => {
  const { hasValue, isDisabled, isFocused, isReadonly } = useFormFieldContext();
  return (
    <StyledLabel
      for={forId}
      hasValue={hasValue}
      disabled={isDisabled}
      isFocused={isFocused}
      readonly={isReadonly}
      {...props}
    />
  );
};
